/**
 * THE APP, AS DATA (client half).
 *
 * This file is the whole client-side answer to "what does this product mount?".
 * Adding a kernel feature is one line in `appComposition`; removing it is
 * deleting that line.
 */
import type { MessageDescriptor } from "@lingui/core";
import { History } from "@fleet/icons";
import type { Accessor, Component } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@mealplan/powersync";
import {
  composition,
  type Composition,
  type PlatformTarget,
  plugged,
  type Plugin,
} from "@fleet/kernel";
import { browserDelivery } from "@fleet/export";
import {
  type ActivityHistoryService,
  activityPlugin,
  PLATFORM_ACTIVITY_HISTORY,
  PLATFORM_POWERSYNC,
  type PowerSyncService,
} from "@fleet/plugins/activity";
import { exportPlugin, tauriDelivery } from "@fleet/plugins/export";
import { notificationsPlugin } from "@fleet/plugins/notifications";
import { adminPlugin } from "@fleet/plugins/admin";
import { clearAllScheduledReminders } from "@/lib/notifications/service";
import { adminRoute } from "@/routes/_app/admin";
import { adminIndexRoute } from "@/routes/_app/admin.index";
import { adminUsersRoute } from "@/routes/_app/admin.users";
import { adminAuditLogRoute } from "@/routes/_app/admin.audit-log";
import { adminIntegrationsRoute } from "@/routes/_app/admin.integrations";
import { adminFlagsRoute } from "@/routes/_app/admin.flags";
import { adminMetricsRoute } from "@/routes/_app/admin.metrics";
import { adminStorageRoute } from "@/routes/_app/admin.storage";
import { adminThemeAuditRoute } from "@/routes/_app/admin.theme-audit";
import { adminThemesRoute } from "@/routes/_app/admin.themes";
import { adminFontsRoute } from "@/routes/_app/admin.fonts";
import { adminEmailRoute } from "@/routes/_app/admin.email";
import { adminEmailCampaignsRoute } from "@/routes/_app/admin.email.campaigns";
import { activityRoute } from "@/routes/_app/activity";
import { fetchOlderActivity } from "@/lib/api-activity-history";
import { deepLinkingPlugin } from "@/plugins/deep-linking";
import { UNDO_BINDING, UNDO_COMMAND } from "@/lib/shortcuts/defaults";
import type { Command, KeyBinding } from "@fleet/shortcuts";

interface AppNavItem {
  id: string;
  to: string;
  order: number;
  label: MessageDescriptor;
  icon: Component<{ class?: string }>;
}

export type AppContribution =
  | { kind: "nav"; item: AppNavItem }
  | { kind: "command"; command: Command; binding?: KeyBinding };

export interface HostServices {
  translate: (message: MessageDescriptor) => string;
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  onHistoryError: () => void;
}

function platformPlugin(host: HostServices): Plugin<void, AppContribution> {
  return {
    id: "platform",
    provides: [PLATFORM_POWERSYNC, PLATFORM_ACTIVITY_HISTORY],
    data(ctx) {
      const powerSync: PowerSyncService = {
        db: host.db,
        drizzle: host.drizzle,
        userId: host.userId,
      };
      ctx.provide(PLATFORM_POWERSYNC, powerSync);

      const history: ActivityHistoryService = {
        fetchOlder: async (before, limit, filter) => fetchOlderActivity(before, limit, filter),
      };
      ctx.provide(PLATFORM_ACTIVITY_HISTORY, history);
    },
  };
}

const adminRoutes = [
  adminRoute.addChildren([
    adminIndexRoute,
    adminUsersRoute,
    adminAuditLogRoute,
    adminIntegrationsRoute,
    adminFlagsRoute,
    adminMetricsRoute,
    adminStorageRoute,
    adminThemeAuditRoute,
    adminThemesRoute,
    adminFontsRoute,
    adminEmailRoute,
    adminEmailCampaignsRoute,
  ]),
] as const;

export const pluginRoutes = [activityRoute, ...adminRoutes] as const;

export const NO_HOST: HostServices = {
  translate: () => {
    throw new Error("composition read without a host: routes pass only");
  },
  db: () => null,
  drizzle: () => null,
  userId: () => undefined,
  onHistoryError: () => {
    throw new Error("composition read without a host: routes pass only");
  },
};

export function appComposition(
  host: HostServices,
  target: PlatformTarget,
): Composition<AppContribution> {
  return composition<AppContribution>({ name: "mealplan", target }, [
    plugged(platformPlugin(host), undefined),
    // Feature #10: scheduled notifications as a plugin. Cancels OS reminders on unmount.
    plugged(notificationsPlugin, { onTeardown: clearAllScheduledReminders }),
    plugged(
      activityPlugin<AppContribution>(),
      {
        contributes: [
          {
            kind: "nav",
            item: {
              id: "activity",
              to: "/activity",
              order: 40,
              label: { id: "Activity", message: "Activity" },
              icon: History,
            },
          },
          {
            kind: "command",
            command: UNDO_COMMAND,
            binding: UNDO_BINDING,
          },
        ],
      },
      [activityRoute],
    ),
    // Feature #8: admin routes and contributions.
    plugged(adminPlugin, undefined, adminRoutes),
    plugged(exportPlugin, { browser: browserDelivery, fileSystem: tauriDelivery }),
    plugged(deepLinkingPlugin, { translate: host.translate }),
  ]);
}
